import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import TickerWithName from "../components/TickerWithName";
import api from "../utils/api";
import { QuickTradeEquityButton } from "../components/QuickTradeButtons";

// -------- shared UI helpers (local to this file) --------
const money2 = (v) => {
    if (v === null || v === undefined || v === "") return "0.00";
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : String(v);
};

const pctChange = (entry, exit) => {
    const a = Number(entry),
        b = Number(exit);
    if (!Number.isFinite(a) || !Number.isFinite(b) || a === 0) return null;
    return ((b - a) / a) * 100;
};

const EstExitLine = ({ entry, exit }) => {
    const pct = pctChange(entry, exit);
    const good = pct !== null && pct >= 0;
    return (
        <div className="text-sm">
            Est Exit: ${money2(exit)}{" "}
            {pct !== null && (
                <span
                    className={`ml-1 px-2 py-0.5 rounded text-xs font-semibold ${good ? "bg-green-600" : "bg-red-600"
                        }`}
                >
                    {good ? "+" : ""}
                    {pct.toFixed(2)}%
                </span>
            )}
        </div>
    );
};

function clamp(n, a, b) {
    return Math.max(a, Math.min(b, n));
}

export default function AllNysePicks() {
    const [results, setResults] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [limit, setLimit] = useState(50);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState("score");
    const [order, setOrder] = useState("desc");
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");

    // rebuild/progress UI
    const [rebuild, setRebuild] = useState({
        running: false,
        processed: 0,
        total: 0,
        error: null,
        started_at: null,
        finished_at: null,
        updated_at: null,
    });

    const navigate = useNavigate();
    const pollRef = useRef(null);

    const progressPct = useMemo(() => {
        const t = Number(rebuild.total || 0);
        const p = Number(rebuild.processed || 0);
        if (!t || t <= 0) return null;
        return clamp(Math.round((p / t) * 100), 0, 100);
    }, [rebuild.total, rebuild.processed]);

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    const fetchRebuildStatus = async () => {
        try {
            const { data } = await api.get("/api/admin/rebuild-status", { timeout: 10000 });
            // data is your _rebuild_state
            setRebuild((prev) => ({ ...prev, ...(data || {}) }));
            if (!data?.running) stopPolling();
        } catch (e) {
            // don't spam the UI; keep it quiet unless we're actively rebuilding
            // but do stop polling if it’s failing repeatedly
            // (we'll just show whatever we last had)
            // eslint-disable-next-line no-console
            console.error("rebuild-status failed", e);
        }
    };

    const startPolling = () => {
        if (pollRef.current) return;
        fetchRebuildStatus();
        pollRef.current = setInterval(fetchRebuildStatus, 2000);
    };

    useEffect(() => {
        return () => stopPolling();
    }, []);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);
            setMsg("");

            try {
                // IMPORTANT: must be /api/all-nyse-picks (NOT /all-nyse-picks)
                const { data } = await api.get("/api/all-nyse-picks", {
                    timeout: 25000,
                    params: { page, limit, sort, order },
                });

                if (cancelled) return;

                setResults(data?.results || []);
                setPages(data?.pages || 1);
                setTotal(data?.total || 0);

                if (data?.error) {
                    setMsg(`Server error: ${data.error}`);
                } else if (data?.building) {
                    setMsg("Rebuilding… please wait (progress shown above).");
                    startPolling();
                } else {
                    // if backend isn't building, stop polling
                    stopPolling();
                }
            } catch (e) {
                const status = e?.response?.status;
                const detail =
                    e?.response?.data?.error ||
                    e?.response?.data ||
                    e?.message ||
                    "Network error. Please try again.";
                if (!cancelled) {
                    setMsg(
                        `Request failed${status ? ` (${status})` : ""}: ${typeof detail === "string" ? detail : JSON.stringify(detail)
                        }`
                    );
                }
                // eslint-disable-next-line no-console
                console.error(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [page, limit, sort, order]);

    // If rebuild.running flips true via status polling, keep polling
    useEffect(() => {
        if (rebuild?.running) startPolling();
        else stopPolling();
    }, [rebuild?.running]);

    const canPrev = page > 1;
    const canNext = page < pages;

    return (
        <div className="p-4 sm:p-5">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-blue-400">
                🗂️ All NYSE Picks
            </h2>

            {/* Rebuild Progress */}
            {(rebuild?.running || rebuild?.error || msg?.toLowerCase().includes("rebuilding")) && (
                <div className="mb-4 p-3 rounded-lg border border-gray-700 bg-gray-800">
                    <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-gray-200">
                            {rebuild?.running ? "Rebuilding…" : rebuild?.error ? "Rebuild error" : "Status"}
                        </div>
                        <div className="text-xs text-gray-400">
                            {rebuild?.running && rebuild?.total
                                ? `${Number(rebuild.processed || 0).toLocaleString()} / ${Number(
                                    rebuild.total || 0
                                ).toLocaleString()}`
                                : rebuild?.running
                                    ? "Working…"
                                    : null}
                        </div>
                    </div>

                    {rebuild?.error && (
                        <div className="mt-2 text-sm text-red-300 break-words">Error: {String(rebuild.error)}</div>
                    )}

                    {/* progress bar */}
                    <div className="mt-2">
                        <div className="h-2 w-full bg-gray-700 rounded">
                            <div
                                className="h-2 bg-blue-500 rounded"
                                style={{
                                    width: progressPct === null ? "25%" : `${progressPct}%`,
                                    transition: "width 250ms ease",
                                }}
                            />
                        </div>
                        <div className="mt-1 text-xs text-gray-400">
                            {progressPct === null
                                ? "Rebuilding…"
                                : `Progress: ${progressPct}%`}
                        </div>
                    </div>
                </div>
            )}

            {/* Controls */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <label className="text-sm">
                    Show
                    <select
                        className="ml-2 bg-gray-800 border border-gray-700 rounded px-2 py-1"
                        value={limit}
                        onChange={(e) => {
                            setPage(1);
                            setLimit(parseInt(e.target.value, 10));
                        }}
                    >
                        {[25, 50, 100, 150, 200].map((n) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </select>
                    / page
                </label>

                <label className="text-sm">
                    Sort by
                    <select
                        className="ml-2 bg-gray-800 border border-gray-700 rounded px-2 py-1"
                        value={sort}
                        onChange={(e) => {
                            setPage(1);
                            setSort(e.target.value);
                        }}
                    >
                        <option value="score">Score</option>
                        <option value="ticker">Ticker</option>
                    </select>
                </label>

                <label className="text-sm">
                    Order
                    <select
                        className="ml-2 bg-gray-800 border border-gray-700 rounded px-2 py-1"
                        value={order}
                        onChange={(e) => {
                            setPage(1);
                            setOrder(e.target.value);
                        }}
                    >
                        <option value="desc">Desc</option>
                        <option value="asc">Asc</option>
                    </select>
                </label>

                <div className="text-sm text-gray-400 ml-auto">
                    Total: {Number(total || 0).toLocaleString()} | Page {page} of {pages}
                </div>
            </div>

            {/* List / Messages */}
            {loading && <div className="text-gray-400">Loading…</div>}
            {msg && !loading && <div className="text-gray-300">{msg}</div>}

            <div className="space-y-3">
                {results.map((r, i) => (
                    <div
                        key={`${r.ticker}-${i}`}
                        className="bg-gray-700 p-3 sm:p-4 rounded-lg shadow hover:bg-gray-600 cursor-pointer"
                        onClick={() => navigate(`/chart/${r.ticker}`)}
                    >
                        <div className="text-lg sm:text-xl font-semibold">
                            <TickerWithName symbol={r.ticker} />
                        </div>
                        <div className="text-sm text-gray-300">Score: {r.score}</div>
                        <div className="text-sm">Entry: ${money2(r.entryPrice)}</div>
                        <EstExitLine entry={r.entryPrice} exit={r.estimatedExit} />
                        <div className="text-sm text-gray-400">{r.reason}</div>
                        <QuickTradeEquityButton ticker={r.ticker} />
                    </div>
                ))}

                {!loading && !msg && results.length === 0 && (
                    <div className="text-gray-400">No results.</div>
                )}
            </div>

            {/* Pagination */}
            <div className="flex items-center gap-2 mt-6">
                <button
                    disabled={!canPrev}
                    onClick={() => setPage(1)}
                    className={`px-3 py-1 rounded bg-gray-800 border border-gray-700 ${!canPrev ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-700"
                        }`}
                >
                    « First
                </button>
                <button
                    disabled={!canPrev}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={`px-3 py-1 rounded bg-gray-800 border border-gray-700 ${!canPrev ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-700"
                        }`}
                >
                    ‹ Prev
                </button>
                <button
                    disabled={!canNext}
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    className={`px-3 py-1 rounded bg-gray-800 border border-gray-700 ${!canNext ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-700"
                        }`}
                >
                    Next ›
                </button>
                <button
                    disabled={!canNext}
                    onClick={() => setPage(pages)}
                    className={`px-3 py-1 rounded bg-gray-800 border border-gray-700 ${!canNext ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-700"
                        }`}
                >
                    Last »
                </button>
            </div>
        </div>
    );
}
